import React, { useState } from 'react';
import { X, Shield, AlertTriangle, CheckCircle, Loader } from 'lucide-react';
import adminApi from '../../services/adminApi';

/**
 * RestoreSuspendedModal - Restores a suspended user account
 * Uses adminApi for proper Authorization header handling
 */
const RestoreSuspendedModal = ({ user, onClose, onSuccess }) => {
  const [step, setStep] = useState('confirm'); // 'confirm' | 'processing' | 'success' | 'error'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reason, setReason] = useState('');

  const handleRestore = async () => {
    setLoading(true);
    setError('');
    setStep('processing');

    try {
      const userId = user._id || user.id;
      console.log('[RestoreModal] Restoring user:', userId);

      // Uses adminApi.request which:
      // - Automatically adds Authorization header (adminAccessToken)
      // - Automatically adds x-2fa-session header
      // - Prepends /api to the endpoint
      const response = await adminApi.request(`/admin/users/${userId}/restore-suspension`, {
        method: 'POST',
        body: JSON.stringify({ reason: reason || 'Admin restoration' })
      });

      console.log('[RestoreModal] Response:', response);

      if (response.success) {
        setStep('success');

        // Auto-close after 2 seconds and call success handler
        setTimeout(() => {
          if (onSuccess) onSuccess(user);
        }, 2000);
      } else {
        throw new Error(response.message || 'Failed to restore account');
      }

    } catch (err) {
      console.error('[RestoreModal] Error:', err);

      let errorMessage = 'Failed to restore account';

      if (err.response?.status === 401) {
        errorMessage = 'Session expired. Please log in again.';
      } else if (err.response?.status === 404) {
        errorMessage = 'User not found.';
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      setStep('error');
      setLoading(false);
    }
  };

  // Don't render if no user
  if (!user) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4">

        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-cyan-600" />
            <h2 className="text-lg font-semibold">Restore Suspended Account</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={loading}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">

          {/* Step 1: Confirm */}
          {step === 'confirm' && (
            <div>
              <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg mb-4">
                <AlertTriangle className="w-6 h-6 text-amber-500 flex-shrink-0" />
                <div>
                  <p className="font-medium text-amber-800">Restore Account</p>
                  <p className="text-sm text-amber-700">
                    This will remove suspension and allow the user to access their account.
                  </p>
                </div>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-500">User</span>
                  <span className="font-medium">{user.fullName || user.full_name || user.firstName || user.email}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-500">Email</span>
                  <span className="font-medium">{user.email}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-500">Type</span>
                  <span className="font-medium capitalize">{user.entityType || user.entity_type || 'Individual'}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-500">Status</span>
                  <span className="font-medium text-red-600">Suspended</span>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason for Restoration (optional)
                </label>
                <input
                  type="text"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g., Appeal approved, Demo account"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                />
              </div>

              <button
                onClick={handleRestore}
                className="w-full py-3 bg-cyan-600 text-white font-medium rounded-lg hover:bg-cyan-700 transition"
              >
                Restore Account
              </button>
            </div>
          )}

          {/* Step 2: Processing */}
          {step === 'processing' && (
            <div className="text-center py-8">
              <Loader className="w-12 h-12 mx-auto mb-4 text-cyan-600 animate-spin" />
              <h3 className="text-lg font-semibold mb-2">Restoring Account...</h3>
              <p className="text-gray-600">Please wait while we restore the account.</p>
            </div>
          )}

          {/* Step 3: Success */}
          {step === 'success' && (
            <div className="text-center py-8">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-green-800 mb-2">Account Restored!</h3>
              <p className="text-gray-600">
                {user.fullName || user.email} can now access their account.
              </p>
            </div>
          )}

          {/* Step 4: Error */}
          {step === 'error' && (
            <div className="text-center py-8">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                <X className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-xl font-semibold text-red-800 mb-2">Restoration Failed</h3>
              <p className="text-gray-600 mb-6">{error}</p>
              <div className="flex gap-3 justify-center">
                <button
                  onClick={() => { setStep('confirm'); setError(''); }}
                  className="px-6 py-2 bg-cyan-600 text-white font-medium rounded-lg hover:bg-cyan-700 transition"
                >
                  Try Again
                </button>
                <button
                  onClick={onClose}
                  className="px-6 py-2 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 transition"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default RestoreSuspendedModal;
